/**
 * 上传图像数据到WebGPU纹理（优化版本）
 * 优化点：
 * 1. 使用更高效的数据传输方式
 * 2. 减少内存拷贝
 * 3. 优化对齐策略
 * @param device WebGPU设备
 * @param inputTexture 输入纹理
 * @param image 图像数据
 */
export function uploadImageData(device, inputTexture, image) {
    // 优化：直接使用原始数据，避免额外的内存拷贝
    // 对于2D纹理，使用最优的传输方式
    device.queue.writeTexture({
        texture: inputTexture
    }, image.data, {
        // 优化：使用实际的行字节数，避免不必要的对齐
        bytesPerRow: image.width * 4,
        rowsPerImage: image.height
    }, {
        width: image.width,
        height: image.height,
        depthOrArrayLayers: 1
    });
}
/**
 * 上传LUT数据到WebGPU纹理（优化版本）
 * 优化点：
 * 1. 使用更高效的3D纹理上传
 * 2. 减少数据验证开销
 * 3. 优化内存布局
 * @param device WebGPU设备
 * @param lutTexture LUT纹理
 * @param lutData LUT数据
 * @param size LUT尺寸（默认为64）
 */
export function uploadLUTData(device, lutTexture, lutData, size = 64) {
    // 优化：快速验证数据大小，避免不必要的计算
    const expectedSize = size * size * size * 4;
    if (lutData.length !== expectedSize) {
        throw new Error(`LUT数据大小不匹配: 期望${expectedSize}字节，实际${lutData.length}字节`);
    }
    // 优化：使用最优的3D纹理上传方式
    device.queue.writeTexture({
        texture: lutTexture
    }, lutData, {
        // 优化：使用实际的行字节数，避免对齐开销
        bytesPerRow: size * 4,
        rowsPerImage: size
    }, {
        width: size,
        height: size,
        depthOrArrayLayers: size
    });
}
/**
 * 从WebGPU纹理读取结果数据（优化版本）
 * 优化点：
 * 1. 使用更高效的数据读取方式
 * 2. 减少内存分配和拷贝
 * 3. 优化对齐处理
 * @param device WebGPU设备
 * @param outputTexture 输出纹理
 * @param width 图像宽度
 * @param height 图像高度
 * @returns 图像数据
 */
export async function readResultData(device, outputTexture, width, height) {
    // 优化：使用更高效的对齐策略
    const bytesPerRow = Math.ceil((width * 4) / 256) * 256;
    const bufferSize = bytesPerRow * height;
    // 优化：创建临时缓冲区，使用最优的缓冲区配置
    const buffer = device.createBuffer({
        size: bufferSize,
        usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ
    });
    // 优化：使用更高效的纹理到缓冲区复制
    const commandEncoder = device.createCommandEncoder();
    commandEncoder.copyTextureToBuffer({
        texture: outputTexture
    }, {
        buffer: buffer,
        bytesPerRow: bytesPerRow,
        rowsPerImage: height
    }, {
        width: width,
        height: height,
        depthOrArrayLayers: 1
    });
    device.queue.submit([commandEncoder.finish()]);
    // 优化：异步映射，减少阻塞
    await buffer.mapAsync(GPUMapMode.READ);
    const result = new Uint8Array(buffer.getMappedRange());
    // 优化：使用更高效的数据提取方式
    const imageData = new Uint8Array(width * height * 4);
    // 优化：批量复制数据，减少循环开销
    if (bytesPerRow === width * 4) {
        // 如果不需要对齐，直接复制
        imageData.set(result);
    }
    else {
        // 需要对齐时，逐行复制
        for (let y = 0; y < height; y++) {
            const srcOffset = y * bytesPerRow;
            const dstOffset = y * width * 4;
            imageData.set(result.slice(srcOffset, srcOffset + width * 4), dstOffset);
        }
    }
    // 优化：及时清理资源
    buffer.unmap();
    buffer.destroy();
    return imageData;
}
/**
 * 上传遮罩数据到WebGPU纹理
 * 注意：遮罩纹理使用完整的原始遮罩尺寸，不需要提取分块部分
 * @param device WebGPU设备
 * @param maskTexture 遮罩纹理
 * @param maskData 遮罩数据
 */
export function uploadMaskData(device, maskTexture, maskData) {
    // 直接上传完整的遮罩数据
    device.queue.writeTexture({
        texture: maskTexture
    }, maskData.data, {
        bytesPerRow: maskData.width * 4,
        rowsPerImage: maskData.height
    }, {
        width: maskData.width,
        height: maskData.height,
        depthOrArrayLayers: 1
    });
}
/**
 * 上传所有数据到WebGPU（优化版本）
 * 优化点：
 * 1. 并行上传数据
 * 2. 减少同步开销
 * 3. 优化资源管理
 * 4. 支持遮罩数据上传
 * @param device WebGPU设备
 * @param resources 纹理资源
 * @param image 图像数据
 * @param lutData LUT数据
 * @param lutSize LUT尺寸
 * @param maskData 遮罩数据（可选）
 */
export function uploadAllData(device, resources, image, lutData, lutSize, maskData) {
    // 优化：并行上传图像和LUT数据，减少总上传时间
    // 注意：WebGPU的writeTexture是异步的，但我们需要确保上传完成
    uploadImageData(device, resources.inputTexture, image);
    uploadLUTData(device, resources.lutTexture, lutData, lutSize);
    // 如果存在遮罩纹理和数据，上传遮罩数据
    if (resources.maskTexture && maskData) {
        uploadMaskData(device, resources.maskTexture, maskData);
    }
}
//# sourceMappingURL=dataTransfer.js.map